<template>
  <div
    class="task"
    :class="{ 'is-new': isNewTask, 'is-read-only': task.readOnly }"
  >
    <div class="task__drag-handle" />
    <div
      v-tooltip="{
        content: task.getReadOnlyHint(itemTeamType, itemConfig),
        theme: 'info-tooltip',
        placement: 'top',
        popperClass: 'story__title__tooltip',
        disabled: !task.readOnly
      }"
      class="task__inner"
    >
      <BaseSubHeading
        class="task__number"
        weight="400"
        size="medium"
        color="monochrome-05"
        darkColor="monochrome-04"
      >
        {{ task.order }}
      </BaseSubHeading>
      <div class="task__name custom-outline">
        <input
          :id="`name_${index}`"
          ref="nameInput"
          v-focus="!isOpen ? isActive(`name_${index}`) : false"
          v-tooltip="{
            content: task.name,
            theme: 'info-tooltip',
            placement: 'bottom',
            popperClass: 'story__title__tooltip',
            skidding: 24,
            disabled: !showTooltipName
          }"
          :value="task.name"
          type="text"
          placeholder="Enter Task Name"
          autocomplete="off"
          class="task__input"
          :class="{ 'pointer-none': dragItem }"
          :readonly="isReadonly"
          @input="handleNameInput($event)"
          @keydown.enter="enter($event)"
          @focusin="focusIn(`name_${index}`)"
          @click.stop
        />
        <button
          v-if="showChecklistIndicator"
          class="task__checklist-indicator"
          type="button"
          :title="task.checklistFullyChecked ? 'Checklist complete' : 'Unchecked checklist items'"
          @mousedown="checkSelectedTask"
        >
          <icon
            data="@icon/task-check-list.svg"
            width="var(--icon-24)"
            class="common-gray-interactive"
            height="var(--icon-24)"
          />
        </button>
        <button
          v-if="taskNameLength && showAddDescriptionButton && !showChecklistIndicator"
          class="task__add-description"
          tabindex="-1"
          :class="{
            'task__add-description_visible': addDescriptionButton,
            'task__add-description_hidden': isOpen
          }"
          type="button"
          @mousedown="checkSelectedTask"
        >
          <icon
            data="@icon/dots-horizontal-bottom.svg"
            :fill="true"
            color="#336DFF"
            width="24"
            height="24"
          />
        </button>
        <ExpectedDeliveryIndicator
          v-if="showExpectedDeliveryIndicator"
          class="task__delivery-date-indicator"
          :hasExpectedDeliveryDate="task.hasExpectedDeliveryDate"
          :formattedExpectedDeliveryDate="task.formattedExpectedDeliveryDate"
          :isExpectedDeliveryDateOverdue="task.isExpectedDeliveryDateOverdue"
          iconSize="var(--icon-24)"
          @mousedown="checkSelectedTask"
        />
        <span
          v-if="cannotBeDeleted && !isNewTask"
          class="error-text error-text-small is-left"
        >
          Unable to delete a Task. At least one task must exist in an Item
        </span>
        <span
          v-if="showNameRequiredError"
          class="error-text error-text-small is-left"
        >
          Task Name is required
        </span>
      </div>

      <button
        v-if="!isNewTask"
        :id="`type_${index}`"
        v-focus="isActive(`type_${index}`)"
        class="task_focus"
        :class="{ 'is-disabled': isTaskTypeReadonly || isReadonly }"
        type="button"
        tabindex="-1"
      >
        <DropdownBasic
          :id="`typeParsed_${index}`"
          :value="taskType"
          :options="availableTaskTypes"
          :index="index"
          :hideSelected="false"
          :placeholder="placeholderForType"
          trackBy="id"
          :tabindex="-1"
          label="label"
          class="task__type custom-outline dropdown-basic"
          :class="[
            { viewPlaceholder: !searchQuery, task__type_disable: !taskNameLength },
            { 'pointer-none': isReadonly }
          ]"
          noResult="No elements found."
          :appendDropDownToBody="true"
          @onSelect="onSelectType"
          @checkOpenDropdown="checkOpenDropdown"
          @dropdownInput="dropdownInput"
          @click.stop
        />
        <div
          v-if="!searchQuery"
          class="searchQuery"
        >
          {{ dropdownSearch }}<span class="searchQuery-clue">{{ dropdownSearchClue }}</span>
        </div>
      </button>

      <div
        v-if="!isNewTask"
        class="task__hours"
        :class="{ alertEstimation: alertEstimationHours || !task.isEstimated }"
      >
        <input
          :id="`estimatedHours_${index}`"
          v-focus="isActive(`estimatedHours_${index}`)"
          type="text"
          class="task__input custom-outline task__estimation"
          maxlength="2"
          :value="estimation"
          :readonly="isReadonly"
          placeholder="--"
          autocomplete="off"
          @input="handleChanges($event.target as HTMLInputElement)"
          @keypress="onHoursValidate($event)"
          @click.stop
        />
      </div>

      <AvatarSelect
        :id="`owner_${index}`"
        v-model:user.sync="task.owner"
        :class="[{ task__owner_disable: !taskNameLength }]"
        :teamId="teamId"
        :focusable="false"
        :dropdownPosition="avatarPosition"
        :customDarkBgColor="customDarkBgColor"
        :customLightThemeBgColor="customLightThemeBgColor"
        :isReadonly="isReadonly"
        @selected="handleChanges"
        @click.stop
      />

      <DropdownTooltip
        class="task__menu"
        :class="[{ task__menu_disable: !taskNameLength || isReadonly }]"
        subDropdownClass="task__menu-sub"
        subMenuClass="task__menu__item"
        iconSize="md"
        placement="bottom-end"
        placementSub="left-start"
        iconSizeSub="md"
        :distanceSub="16"
        :menu="moreOptions"
        :disabledSub="selectOption !== 'moveTo'"
        :tabIndex="-1"
        @dropdownSubMenu="dropdownSubMenu"
        @click.stop
      >
        <template #tooltip-subContent>
          <TaskMoveTo
            :teamId="teamId"
            @taskMove="moveTo"
          />
        </template>
      </DropdownTooltip>
    </div>

    <BaseCaption
      v-if="alertEstimationHours"
      size="small"
      color="serviceAlert-02"
      className="task__validation"
    >
      Could this task be broken down into smaller tasks to be less than 24 hours in size?
    </BaseCaption>
  </div>
</template>

<script lang="ts">
import { mixins, Options } from "vue-class-component"
import { Emit, Prop } from "vue-property-decorator"
import { directive as ClickOutside } from "click-outside-vue3"
import { Getter } from "s-vuex-class"

import AvatarSelect from "@/components/Avatar/AvatarSelect.vue"
import DropdownBasic from "@/components/Dropdown/DropdownBasic.vue"
import DropdownTooltip from "@/components/Dropdown/DropdownTooltip.vue"
import ExpectedDeliveryIndicator from "@/components/ExpectedDeliveryIndicator.vue"
import TaskMoveTo from "@/components/Task/TaskMoveTo.vue"

import KeyboardNavigation from "@/mixins/keyboardNavigation"
import {
  CallbackFunction,
  ItemConfigs,
  MoveToInfo,
  Task,
  TaskOptions,
  TaskTypeOption
} from "@/models"
import { SubMenu } from "@/models/SubMenu"
import { TeamType } from "@/models/Team"
import { Getters } from "@/store"
import { inputOnlyNumber, replaceHtmlTags } from "@/utils/helpers"

const components = {
  AvatarSelect,
  DropdownBasic,
  DropdownTooltip,
  TaskMoveTo,
  ExpectedDeliveryIndicator
}

@Options({
  name: "TaskItem",
  components,
  directives: { ClickOutside }
})
export default class TaskItem extends mixins(KeyboardNavigation) {
  @Getter(Getters.GET_ITEM_TEAM_TYPE) readonly itemTeamType: TeamType
  @Getter(Getters.GET_ITEM_CONFIG) readonly itemConfig: ItemConfigs

  @Prop({ required: true }) readonly task: Task
  @Prop({ required: true }) readonly maxTaskNameLength: number
  @Prop({ default: 0 }) readonly index: number
  @Prop({ default: "" }) readonly teamId: string
  @Prop({ default: "right" }) readonly avatarPosition: string
  @Prop({ default: false }) readonly isOpen: boolean
  @Prop({ default: false }) readonly dragItem: boolean
  @Prop({ default: false }) readonly isNewTask: boolean
  @Prop({ default: false }) readonly isOnlyOneTaskInActiveSprint: boolean
  @Prop({ default: false }) readonly cannotBeDeleted: boolean
  @Prop({ default: "" }) readonly customDarkBgColor: string
  @Prop({ default: "" }) readonly customLightThemeBgColor: string
  @Prop({ default: false }) readonly isReadonly: boolean
  @Prop({ default: false }) readonly isTaskTypeReadonly: boolean
  @Prop({ default: [] }) readonly availableTaskTypes: TaskTypeOption[]
  @Prop({ default: null }) readonly taskTypeOption: TaskTypeOption
  @Prop({ default: true }) readonly showAddDescriptionButton: boolean
  @Prop({ default: false }) readonly showNameRequiredError: boolean

  dropdownSearch = ""
  dropdownSearchClue = ""
  searchQuery = true
  selectOption = ""
  showTooltipName = false

  get moreOptions(): SubMenu[] {
    let options = [
      {
        id: "addDetails",
        label: "Edit Details"
      },
      {
        id: "moveTo",
        label: "Move to..."
      },
      {
        id: "delete",
        label: "Delete"
      }
    ]
    if (this.isOpen) {
      options = options.filter(e => e.id === "delete")
    }
    if (this.isOnlyOneTaskInActiveSprint) {
      options = options.filter(e => e.id !== "delete")
    }
    return options
  }

  get taskType() {
    return this.taskTypeOption ? this.taskTypeOption : this.task.typeParsed
  }

  get addDescriptionButton() {
    return !!replaceHtmlTags(this.task.structuredDescription?.html ?? "")
  }

  /** The row shows checklist state without requiring the Task details to be opened. */
  get hasChecklist() {
    return (this.task.structuredDescription?.html ?? "").includes('data-type="taskList"')
  }

  get showChecklistIndicator() {
    return this.hasChecklist && this.task.estimatedHours !== 0
  }

  get taskNameLength() {
    return this.itemName.length
  }

  get estimation() {
    if (this.task.estimatedHours || this.task.estimatedHours == 0) return this.task.estimatedHours
    return ""
  }

  // eslint-disable-next-line getter-return
  get placeholderForType() {
    if (this.task.typeParsed) {
      return this.task.typeParsed.label
    }
  }

  get alertEstimationHours() {
    return this.task.estimatedHours ? this.task.estimatedHours >= 24 : ""
  }

  get showExpectedDeliveryIndicator() {
    return this.task.hasExpectedDeliveryDate && this.task.estimatedHours !== 0
  }

  get itemName() {
    return this.task?.name ?? ""
  }

  created() {
    this.idList = [
      {
        id: "name",
        dropdown: false
      },
      {
        id: "type",
        dropdown: true
      },
      {
        id: "estimatedHours",
        dropdown: false
      }
    ]
    if (this.taskTypeOption) {
      this.onSelectType({ id: "typeParsed", value: this.taskTypeOption })
    }
  }

  mounted() {
    this.checkShowTooltipName()
    window.addEventListener("resize", this.checkShowTooltipName)
  }

  beforeUnmount() {
    window.removeEventListener("resize", this.checkShowTooltipName)
  }

  checkShowTooltipName() {
    const inputElement = this.$refs.nameInput as HTMLInputElement
    this.showTooltipName = inputElement.offsetWidth < inputElement.scrollWidth
  }

  onHoursValidate(event: KeyboardEvent) {
    inputOnlyNumber(event)
  }

  removeDropdownOption(searchQuery) {
    if (searchQuery) {
      this.searchQuery = false
    } else {
      this.searchQuery = true
    }
  }

  dropdownInput(searchQuery) {
    const searchQuerylLowerCase = searchQuery.toLowerCase()
    this.removeDropdownOption(searchQuery)
    this.dropdownSearch = searchQuery
    let checkFilterTypes = this.availableTaskTypes
      .map(a => a.label.toLowerCase())
      .filter(a => a.match(searchQuerylLowerCase)?.input)
    if (checkFilterTypes.length) {
      this.availableTaskTypes.forEach(element => {
        const taskTypesList = element.label.toLowerCase().split(" ").join("")
        if (
          taskTypesList.match(searchQuerylLowerCase)?.[0] &&
          searchQuerylLowerCase.length >=
            Math.floor(taskTypesList.length / 2 >= 4 ? 4 : taskTypesList.length / 2)
        ) {
          this.dropdownSearchClue = taskTypesList.replace(searchQuerylLowerCase, "")
        } else if (!searchQuery.length) {
          this.dropdownSearchClue = ""
        }
      })
    } else {
      this.dropdownSearchClue = ""
    }
  }

  onSelectType({ value, id }: { id: string; value: TaskOptions | string }) {
    this.handleChanges({ value, id })
  }

  enter(e) {
    let checkSystemKey = this.isMacintosh ? e.metaKey : e.ctrlKey
    e.preventDefault()
    if (checkSystemKey || e.altKey) {
      this.checkSelectedTask()
    }
  }

  dropdownSubMenu({ id, hide }) {
    this.selectOption = id
    if (id === "delete") {
      this.onDropdownOption({ id: id }, hide)
    }
    if (id === "addDetails") {
      this.checkSelectedTask()
    }
  }

  handleNameInput(e: Event) {
    const target = e.target as HTMLInputElement
    const cursorStart = target.selectionStart
    const cursorEnd = target.selectionEnd
    this.handleChanges(target)
    this.$nextTick(() => {
      if (this.taskNameLength > this.maxTaskNameLength) {
        this.checkSelectedTask()
      }
      target.setSelectionRange(cursorStart, cursorEnd)
      this.checkShowTooltipName()
    })
  }

  @Emit("update")
  handleChanges({ value, id }: { id: string; value: TaskOptions | string }) {
    let idSplit = id.split("_")[0]
    if (id.includes("typeParsed") && value === null) return false
    this.$nextTick(() => {
      if (this.taskNameLength > this.maxTaskNameLength && idSplit === "name") {
        this.checkSelectedTask()
      }
    })

    return {
      value,
      id: idSplit,
      index: this.index
    }
  }
  @Emit("options")
  onDropdownOption({ value, id }: { id: string; value?: TaskOptions }, hide: CallbackFunction) {
    hide()
    return {
      value,
      id,
      index: this.index
    }
  }

  @Emit("checkSelectedTask")
  checkSelectedTask() {
    return {
      index: this.index,
      opened: true
    }
  }

  @Emit()
  moveTo({
    moveToInfo,
    sprintId,
    teamId
  }: {
    moveToInfo: MoveToInfo
    sprintId: string
    teamId: string
  }) {
    return {
      moveToInfo,
      sprintId,
      task: this.task,
      teamId
    }
  }

  @Emit("saveTask")
  saveTask() {}

  focusIn(id) {
    this.activateFocus(id)
  }
}
</script>
